import { apiSlice } from '../app/api/apiSlice';

export const customerApiSlice = apiSlice.enhanceEndpoints({ addTagTypes: ['DashboardFormula', 'DashboardSummary', 'DashboardIngredients', 'CustomerSubscriptions'] }).injectEndpoints({
  endpoints: (builder) => ({
    spinRetentionGift: builder.mutation({
      query: (body) => ({ url: 'customer/retention-gift/spin', method: 'POST', body }),
      invalidatesTags: ['DashboardSummary', 'CustomerSubscriptions'],
    }),
    getDashboardFormula: builder.query({
      query: ({ lang, userSubscriptionId }) => ({
        url: `customer/dashboard/formula?lang=${encodeURIComponent(lang)}${userSubscriptionId ? `&userSubscriptionId=${encodeURIComponent(userSubscriptionId)}` : ''}`,
      }),
      providesTags: ['DashboardFormula'],
    }),
    updateDashboardFormula: builder.mutation({
      query: (body) => ({ url: 'customer/dashboard/formula', method: 'POST', body }),
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'DashboardIngredients', 'CustomerSubscriptions'],
    }),
    getLastQuiz: builder.mutation({
      query: (payload) => ({
        url: 'customer/lastquizdetail',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getDashboardSummary: builder.query({
      providesTags: ['DashboardSummary'],
      query: (params = {}) => {
        const queryString = new URLSearchParams(
          Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== ''),
        ).toString();

        return {
          url: queryString ? `customer/dashboard/summary?${queryString}` : 'customer/dashboard/summary',
        };
      },
      validateStatus: (response, result) => response.status === 200 && !result?.isError,
    }),
    getDashboardIngredients: builder.query({
      providesTags: ['DashboardIngredients'],
      query: (params = {}) => {
        const queryString = new URLSearchParams(
          Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== ''),
        ).toString();

        return {
          url: queryString ? `customer/dashboard/ingredients?${queryString}` : 'customer/dashboard/ingredients',
        };
      },
      validateStatus: (response, result) => response.status === 200 && !result?.isError,
    }),
    compareQuizWeb: builder.mutation({
      query: (payload) => ({
        url: 'customer/compareQuizWeb',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    updateQuizVerWeb: builder.mutation({
      query: (payload) => ({
        url: 'customer/updateQuizVerWeb',
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getUserSubscription: builder.query({
      providesTags: ['CustomerSubscriptions'],
      query: () => ({ url: `customer/getUserSubscription` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    cancelSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/cancel/stripe`,
        method: 'POST',
        body: payload,
      }),
    }),
    pauseSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/pause/stripe`,
        method: 'POST',
        body: payload,
      }),
    }),
    resumeSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/resume/stripe`,
        method: 'POST',
        body: payload,
      }),
    }),
    restartSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/restart/stripe`,
        method: 'POST',
        body: payload,
      }),
    }),
    applyCouponToSubscription: builder.mutation({
      query: (payload) => ({
        url: `/customer/subscription/apply-coupon`,
        method: 'POST',
        body: payload,
      }),
    }),
    requestFormulaReview: builder.mutation({
      query: (payload) => ({
        url: `/customer/subscription/request-formula-review`,
        method: 'POST',
        body: payload,
      }),
    }),
    addOmega3ToSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/items`,
        method: 'POST',
        body: payload,
      }),
    }),
    addOmega3UpsellToSubscription: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `/customer/subscription/items/upsell`,
        method: 'POST',
        body: payload,
      }),
    }),
    /** Post-checkout upsell: no JWT; server verifies orderId + quizId */
    addOmega3UpsellGuest: builder.mutation({
      query: (payload) => ({
        url: `order/omega3-upsell`,
        method: 'POST',
        body: payload,
      }),
    }),
    handlePostPurchaseUpsell: builder.mutation({
      query: (payload) => ({
        url: `order/post-purchase-upsell`,
        method: 'POST',
        body: payload,
      }),
    }),
    removeSubscriptionItem: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (itemId) => ({
        url: `/customer/subscription/items/${itemId}`,
        method: 'DELETE',
      }),
    }),
    getPlanChangeOptions: builder.query({
      query: ({ userSubscriptionId }) => ({
        url: `customer/subscription/plan-change-options?userSubscriptionId=${userSubscriptionId}`,
      }),
      validateStatus: (response, result) => response.status === 200 && !result?.isError,
    }),
    changePlan: builder.mutation({
      invalidatesTags: ['DashboardFormula', 'DashboardSummary', 'CustomerSubscriptions'],
      query: (payload) => ({
        url: `customer/subscription/change-plan`,
        method: 'POST',
        body: payload,
      }),
    }),
    getCustomerReferrals: builder.query({
      query: () => ({
        url: `customer/referrals`,
      }),
      validateStatus: (response, result) => response.status === 200 && !result?.isError,
    }),
    sendReferralInvite: builder.mutation({
      query: (payload) => ({
        url: `customer/referrals/email`,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  useSpinRetentionGiftMutation,
  useGetDashboardFormulaQuery,
  useUpdateDashboardFormulaMutation,
  useGetLastQuizMutation,
  useGetDashboardSummaryQuery,
  useGetDashboardIngredientsQuery,
  useCompareQuizWebMutation,
  useUpdateQuizVerWebMutation,
  useGetUserSubscriptionQuery,
  useCancelSubscriptionMutation,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation,
  useRestartSubscriptionMutation,
  useApplyCouponToSubscriptionMutation,
  useRequestFormulaReviewMutation,
  useAddOmega3ToSubscriptionMutation,
  useAddOmega3UpsellToSubscriptionMutation,
  useAddOmega3UpsellGuestMutation,
  useHandlePostPurchaseUpsellMutation,
  useRemoveSubscriptionItemMutation,
  useGetPlanChangeOptionsQuery,
  useChangePlanMutation,
  useGetCustomerReferralsQuery,
  useSendReferralInviteMutation,
} = customerApiSlice;
